<script setup lang="ts">
import Player from '@vimeo/player';
import { onMounted, ref, onBeforeUnmount } from 'vue';
import { useQuasar } from 'quasar';

const $q = useQuasar();

interface Props {
  vimeoId: number;
}

const props = defineProps<Props>();

// Generate a unique ID for each video instance
const playerId = ref(
  `vimeo-player-${props.vimeoId}-${Math.random().toString(36).substr(2, 9)}`,
);
const player = ref<Player | null>(null);
const isLoading = ref(true);
const hasError = ref(false);

// defineEmits<{
//   // 'update:modelValue': [payload: string]
// }>()

onMounted(() => {
  try {
    player.value = new Player(playerId.value, {
      id: props.vimeoId,
      autoplay: true,
      muted: false,
      loop: false,
      controls: true,
      width: $q.screen.gt.sm ? 640 : 320,
      fullscreen: true,
    });

    // Set a timeout as a fallback to hide loading
    const loadingTimeout = setTimeout(() => {
      if (isLoading.value) {
        console.log('Loading timeout reached, hiding spinner');
        isLoading.value = false;
      }
    }, 3000);

    // Listen for multiple events to hide loading spinner
    const hideLoading = () => {
      clearTimeout(loadingTimeout);
      isLoading.value = false;
    };

    player.value.on('loaded', hideLoading);
    player.value.on('playing', hideLoading);
    player.value.on('play', hideLoading);

    // Listen for error events
    player.value.on('error', (error) => {
      console.error('Vimeo player error:', error);
      clearTimeout(loadingTimeout);
      isLoading.value = false;
      hasError.value = true;
    });
  } catch (error) {
    console.error('Failed to initialize Vimeo player:', error);
    isLoading.value = false;
    hasError.value = true;
  }
});

onBeforeUnmount(() => {
  // Clean up the player instance when component is destroyed
  if (player.value) {
    void player.value.destroy();
  }
});
</script>

<template>
  <q-card>
    <q-card-section class="relative-position">
      <!-- Loading skeleton/spinner -->
      <div
        v-if="isLoading"
        class="absolute-full flex flex-center video-overlay"
        style="z-index: 10"
      >
        <div class="column items-center q-gutter-md">
          <q-spinner-dots
            color="primary"
            size="50px"
          />
          <div class="text-caption text-muted">{{ $t('VIDEO_LOADING') }}</div>
        </div>
      </div>

      <!-- Error message -->
      <div
        v-if="hasError && !isLoading"
        class="absolute-full flex flex-center video-overlay"
        style="z-index: 10"
      >
        <div class="column items-center q-gutter-md text-muted">
          <q-icon
            name="error_outline"
            size="50px"
          />
          <div class="text-caption">{{ $t('VIDEO_LOAD_ERROR') }}</div>
        </div>
      </div>

      <!-- Video player container -->
      <div
        :id="playerId"
        :style="{ minHeight: $q.screen.gt.sm ? '360px' : '180px' }"
      ></div>
    </q-card-section>
    <slot name="footer" />
  </q-card>
</template>

<style scoped>
/* The loading/error overlays used to be a fixed `bg-grey-2` slab, which stayed
   bright white-grey inside a dark card and left its caption at 4.2:1. Follow
   the card surface instead. */
.video-overlay {
  background: var(--surface-elevated);
}
</style>
